/**
 * Zoom utility functions for handling CSS zoom transformations
 * Based on the app's zoom implementation using CSS zoom property
 */

/**
 * Get the current zoom level from CSS custom property or computed style
 * @returns {number} Current zoom factor (1.0 = 100% zoom)
 */
export const getZoomLevel = () => {
  try {
    // First try to get from CSS custom property (preferred method)
    const rootStyles = getComputedStyle(document.documentElement);
    const cssZoom = rootStyles.getPropertyValue('--zoom').trim();

    if (cssZoom && !Number.isNaN(parseFloat(cssZoom))) {
      return parseFloat(cssZoom);
    }

    // Fallback: get from computed zoom property
    const computedZoom = parseFloat(rootStyles.zoom);
    return Number.isNaN(computedZoom) ? 1.0 : Math.max(0.1, computedZoom);
  } catch (error) {
    console.warn('Failed to detect zoom level:', error);
    return 1.0;
  }
};

/**
 * Convert zoomed coordinates to unzoomed (normalized) coordinates
 * @param {number} zoomedCoord - Coordinate in zoomed space
 * @returns {number} Coordinate in unzoomed space
 */
export const unzoomCoordinate = (zoomedCoord) => {
  const zoom = getZoomLevel();
  return zoomedCoord / zoom;
};

/**
 * Convert unzoomed coordinates to zoomed coordinates
 * @param {number} unzoomedCoord - Coordinate in unzoomed space
 * @returns {number} Coordinate in zoomed space
 */
export const zoomCoordinate = (unzoomedCoord) => {
  const zoom = getZoomLevel();
  return unzoomedCoord * zoom;
};

/**
 * Get zoom-corrected bounding rectangle
 * @param {DOMRect} rect - Original bounding rectangle
 * @returns {Object} Corrected rectangle with unzoomed coordinates
 */
export const getZoomCorrectedRect = (rect) => {
  return {
    top: unzoomCoordinate(rect.top),
    left: unzoomCoordinate(rect.left),
    right: unzoomCoordinate(rect.right),
    bottom: unzoomCoordinate(rect.bottom),
    width: rect.width / getZoomLevel(),
    height: rect.height / getZoomLevel(),
    x: unzoomCoordinate(rect.x || rect.left),
    y: unzoomCoordinate(rect.y || rect.top),
  };
};

/**
 * Get zoom-corrected mouse/touch event coordinates
 * @param {MouseEvent|TouchEvent} event - Mouse or touch event
 * @returns {Object} Corrected coordinates {x, y}
 */
export const getZoomCorrectedEventCoords = (event) => {
  const clientX = event.clientX || (event.touches && event.touches[0]?.clientX) || 0;
  const clientY = event.clientY || (event.touches && event.touches[0]?.clientY) || 0;

  return {
    x: unzoomCoordinate(clientX),
    y: unzoomCoordinate(clientY),
  };
};

/**
 * Check if zoom level has changed
 * @param {number} previousZoom - Previous zoom level to compare against
 * @returns {boolean} True if zoom has changed
 */
export const hasZoomChanged = (previousZoom) => {
  return Math.abs(getZoomLevel() - previousZoom) > 0.001; // Small tolerance for floating point
};

/**
 * Create a zoom change detector hook
 * @param {Function} callback - Function to call when zoom changes
 * @param {number} interval - Check interval in milliseconds (default: 500ms)
 * @returns {Function} Cleanup function
 */
export const createZoomChangeDetector = (callback, interval = 500) => {
  let lastZoom = getZoomLevel();
  let isActive = true;

  const checkZoom = () => {
    if (!isActive) return;

    const currentZoom = getZoomLevel();
    if (hasZoomChanged(lastZoom)) {
      callback(currentZoom, lastZoom);
      lastZoom = currentZoom;
    }
  };

  const intervalId = setInterval(checkZoom, interval);

  // Also listen for window resize as it might indicate zoom changes
  const handleResize = () => {
    setTimeout(checkZoom, 100); // Debounce resize events
  };

  window.addEventListener('resize', handleResize);

  // Cleanup function
  return () => {
    isActive = false;
    clearInterval(intervalId);
    window.removeEventListener('resize', handleResize);
  };
};

/**
 * Scale a value by the current zoom level
 * @param {number} value - Value to scale
 * @returns {number} Scaled value
 */
export const scaleByZoom = (value) => {
  return value / getZoomLevel();
};

/**
 * Unscale a value from zoom (get the "real" size)
 * @param {number} value - Scaled value
 * @returns {number} Unscaled value
 */
export const unscaleByZoom = (value) => {
  return value * getZoomLevel();
};
